/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildExportTrackName,
  CompletedEnhanceSpeechTrack,
  fetchExpiringUrl,
} from "components/EnhancedSpeech/Utilities";

interface ExpiringEnhanceSpeechTrackUrls {
  originalTrackExpiringUrl: string;
  enhanceSpeechTrackExpiringUrl: string;
}

const useBasicAudioPlayer = ({
  track,
  onLoadingSuccess,
  onLoadingFailed,
  startEnhanced = true,
}: {
  track: CompletedEnhanceSpeechTrack;
  onLoadingSuccess?: () => void;
  onLoadingFailed?: (error: MediaError | null) => void;
  startEnhanced?: boolean;
}): {
  isLoaded: boolean;
  enhanced: boolean;
  isPlaying: boolean;
  togglePlayback: () => void;
  toggleEnhance: () => void;
  displayName: string;
} => {
  const [enhanced, setEnhanced] = useState<boolean>(startEnhanced);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expiringTrackUrls, setExpiringTrackUrls] =
    useState<ExpiringEnhanceSpeechTrackUrls | null>();
  const originalAudioRef = useRef<HTMLAudioElement | null>(null);
  const enhancedAudioRef = useRef<HTMLAudioElement | null>(null);

  const enhancedName = buildExportTrackName(track);
  const displayName = enhanced ? enhancedName : track.trackName;

  const fetchExpiringEnhanceSpeechTrack = useCallback(async () => {
    setExpiringTrackUrls({
      enhanceSpeechTrackExpiringUrl: await fetchExpiringUrl(
        track.enhanceSpeechAudioUrl
      ),
      originalTrackExpiringUrl: await fetchExpiringUrl(track.originalAudioUrl),
    });
  }, [track]);

  useEffect(() => {
    if (!expiringTrackUrls) {
      fetchExpiringEnhanceSpeechTrack();
    }
  }, [track, expiringTrackUrls, fetchExpiringEnhanceSpeechTrack]);

  useEffect(() => {
    // ⚠️ WARNING ⚠️:
    // This will not run if the audio element refs change e.g. they end up being conditionally re-rendered.

    let unmounted = false;
    if (!expiringTrackUrls) {
      return;
    }

    if (!enhancedAudioRef.current || !originalAudioRef.current) {
      const enhancedAudioElement = document.createElement("audio");
      enhancedAudioElement.src =
        expiringTrackUrls.enhanceSpeechTrackExpiringUrl;
      enhancedAudioElement.preload = "auto";
      document.body.appendChild(enhancedAudioElement);
      enhancedAudioRef.current = enhancedAudioElement;

      const originalAudioElement = document.createElement("audio");
      originalAudioElement.src = expiringTrackUrls.originalTrackExpiringUrl;
      originalAudioElement.preload = "auto";
      document.body.appendChild(originalAudioElement);
      originalAudioRef.current = originalAudioElement;
    }

    const setIsPlayingFalse = (): void => {
      if (unmounted) {
        return;
      }
      setIsPlaying(false);
    };
    originalAudioRef.current.onended = setIsPlayingFalse;
    enhancedAudioRef.current.onended = setIsPlayingFalse;

    enhancedAudioRef.current.oncanplaythrough = (): void => {
      if (unmounted) {
        return;
      }
      setIsLoaded?.(true);
      onLoadingSuccess?.();
    };

    originalAudioRef.current.onerror = (event: Event): void => {
      if (unmounted) {
        return;
      }
      // TODO: Could we refresh here? It may not always fix it. See `enhance_speech_spec.rb:149`
      // fetchExpiringEnhanceSpeechTrack();
      onLoadingFailed?.((event.target as HTMLMediaElement)?.error);
    };

    enhancedAudioRef.current.onerror = (event: Event): void => {
      if (unmounted) {
        return;
      }
      // TODO: Could we refresh here? It may not always fix it. See `enhance_speech_spec.rb:149`
      // fetchExpiringEnhanceSpeechTrack();
      onLoadingFailed?.((event.target as HTMLMediaElement)?.error);
    };

    enhancedAudioRef.current?.load();
    originalAudioRef.current?.load();

    return (): void => {
      enhancedAudioRef.current?.remove();
      originalAudioRef.current?.remove();
      enhancedAudioRef.current = null;
      originalAudioRef.current = null;
      unmounted = true;
    };
  }, [expiringTrackUrls, onLoadingFailed, onLoadingSuccess, setIsLoaded]);

  const toggleEnhance = (): void => {
    if (originalAudioRef.current && enhancedAudioRef.current) {
      const priorRef = enhanced
        ? enhancedAudioRef.current
        : originalAudioRef.current;
      const newRef = enhanced
        ? originalAudioRef.current
        : enhancedAudioRef.current;

      priorRef.pause();
      newRef.currentTime = priorRef.currentTime;
      isPlaying && newRef.play();
    }

    setEnhanced(!enhanced);
  };

  const togglePlayback = async (): Promise<void> => {
    if (isPlaying) {
      originalAudioRef.current?.pause();
      enhancedAudioRef.current?.pause();
    } else {
      enhanced
        ? await enhancedAudioRef.current?.play()
        : await originalAudioRef.current?.play();
    }

    setIsPlaying(!isPlaying);
  };

  return {
    displayName,
    isLoaded,
    enhanced,
    isPlaying,
    togglePlayback,
    toggleEnhance,
  };
};

export default useBasicAudioPlayer;
